import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateInteractiveFormSessionOptions,
  CreateInteractiveFormSessionStreamedOptions,
  CreateInteractiveFormSessionStreamedResponse,
  Form,
  GenerateFormSummaryApplicationErrors,
  GenerateFormSummaryResponse,
  InteractiveFormSession,
  InteractiveFormSessionCreatedEnvelope,
  SendUserMessageApplicationErrors,
  SendUserMessageOptions,
  SendUserMessageResponse,
  SendUserMessageStreamedApplicationErrors,
  SendUserMessageStreamedOptions,
  SendUserMessageStreamedResponse,
  createInteractiveFormSession as universalCreateInteractiveFormSession,
  createInteractiveFormSessionStreamed as universalCreateInteractiveFormSessionStreamed,
  generateFormSummary as universalGenerateFormSummary,
  sendUserMessage as universalSendUserMessage,
  sendUserMessageStreamed as universalSendUserMessageStreamed,
} from './forms-ai-v1-interactive-form-session-interactive-form-sessions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/forms' };

export function createInteractiveFormSession(
  httpClient: HttpClient
): CreateInteractiveFormSessionSignature {
  return (formId: string, options?: CreateInteractiveFormSessionOptions) =>
    universalCreateInteractiveFormSession(
      formId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateInteractiveFormSessionSignature {
  /**
   * Creates an interactive form session for AI-powered conversational form completion.
   *
   * For implementations that require real-time streaming, call [Create Interactive Form Session Streamed](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/create-interactive-form-session-streamed) instead.
   * @param - Form ID to create an interactive session for.
   * @returns Created interactive form session.
   */
  (formId: string, options?: CreateInteractiveFormSessionOptions): Promise<
    NonNullablePaths<InteractiveFormSession, `_id` | `formId`, 2>
  >;
}

export function createInteractiveFormSessionStreamed(
  httpClient: HttpClient
): CreateInteractiveFormSessionStreamedSignature {
  return (
    formId: string,
    options?: CreateInteractiveFormSessionStreamedOptions
  ) =>
    universalCreateInteractiveFormSessionStreamed(
      formId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateInteractiveFormSessionStreamedSignature {
  /**
   * Creates an interactive form session for AI-powered conversational form completion, with real-time streaming.
   *
   * For implementations that prefer to wait for the complete response, call [Create Interactive Form Session](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/create-interactive-form-session) instead.
   * @param - Form ID to create an interactive session for.
   */
  (
    formId: string,
    options?: CreateInteractiveFormSessionStreamedOptions
  ): Promise<
    NonNullablePaths<
      CreateInteractiveFormSessionStreamedResponse,
      | `responseChunk.textDetails.text`
      | `responseChunk.textDetails.style`
      | `responseChunk.textDataDetails.fieldTarget`
      | `responseChunk.textDataDetails.text`
      | `responseChunk.textDataDetails.displayValue`
      | `responseChunk.multiSelectInputDetails.fieldTarget`
      | `responseChunk.multiSelectInputDetails.options`
      | `responseChunk.multiSelectInputDetails.options.${number}.value`
      | `responseChunk.multiSelectInputDetails.options.${number}.label`
      | `responseChunk.multiSelectInputDetails.allowedCustomValue.description`
      | `responseChunk.numberInputDetails.fieldTarget`
      | `responseChunk.separatorDetails.type`
      | `responseChunk.singleSelectInputDetails.fieldTarget`
      | `responseChunk.singleSelectInputDetails.options`
      | `responseChunk.errorDetails.messageText`
      | `responseChunk.submissionDetails.submissionId`
      | `responseChunk.importantTextDetails.fieldTarget`
      | `responseChunk.importantTextDetails.text`
      | `responseChunk.endOfResponseDetails.success`
      | `responseChunk.fileUploadDetails.fieldTarget`
      | `responseChunk.signatureDetails.fieldTarget`
      | `responseChunk.chunkType`
      | `responseChunk.meaningfulInput.startOffset`
      | `responseChunk.meaningfulInput.length`
      | `interactiveFormSession._id`
      | `interactiveFormSession.formId`,
      6
    >
  >;
}

export function sendUserMessage(
  httpClient: HttpClient
): SendUserMessageSignature {
  return (interactiveFormSessionId: string, options?: SendUserMessageOptions) =>
    universalSendUserMessage(
      interactiveFormSessionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendUserMessageSignature {
  /**
   * Submits a user message to an existing interactive form session and processes the conversational input.
   * User messages support up to 10,000 characters.
   *
   * For implementations that require real-time streaming, call [Send User Message Streamed](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/send-user-message-streamed) instead.
   * @param - Interactive form session ID to send the message to.
   */
  (interactiveFormSessionId: string, options?: SendUserMessageOptions): Promise<
    NonNullablePaths<
      SendUserMessageResponse,
      | `interactiveFormSession._id`
      | `interactiveFormSession.formId`
      | `responseChunks`
      | `responseChunks.${number}.textDetails.text`
      | `responseChunks.${number}.textDetails.style`
      | `responseChunks.${number}.textDataDetails.fieldTarget`
      | `responseChunks.${number}.textDataDetails.text`
      | `responseChunks.${number}.textDataDetails.displayValue`
      | `responseChunks.${number}.multiSelectInputDetails.fieldTarget`
      | `responseChunks.${number}.multiSelectInputDetails.allowedCustomValue.description`
      | `responseChunks.${number}.numberInputDetails.fieldTarget`
      | `responseChunks.${number}.separatorDetails.type`
      | `responseChunks.${number}.singleSelectInputDetails.fieldTarget`
      | `responseChunks.${number}.errorDetails.messageText`
      | `responseChunks.${number}.submissionDetails.submissionId`
      | `responseChunks.${number}.importantTextDetails.fieldTarget`
      | `responseChunks.${number}.importantTextDetails.text`
      | `responseChunks.${number}.endOfResponseDetails.success`
      | `responseChunks.${number}.fileUploadDetails.fieldTarget`
      | `responseChunks.${number}.signatureDetails.fieldTarget`
      | `responseChunks.${number}.chunkType`
      | `responseChunks.${number}.meaningfulInput.startOffset`
      | `responseChunks.${number}.meaningfulInput.length`,
      6
    > & {
      __applicationErrorsType?: SendUserMessageApplicationErrors;
    }
  >;
}

export function sendUserMessageStreamed(
  httpClient: HttpClient
): SendUserMessageStreamedSignature {
  return (
    interactiveFormSessionId: string,
    options?: SendUserMessageStreamedOptions
  ) =>
    universalSendUserMessageStreamed(
      interactiveFormSessionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendUserMessageStreamedSignature {
  /**
   * Submits a user message to an existing interactive form session and processes the conversational input, with real-time streaming.
   *
   * For implementations that prefer to wait for the complete response, call [Send User Message](https://dev.wix.com/docs/api-reference/crm/forms/interactive-form-sessions/send-user-message) instead.
   * @param - Interactive form session ID to send the message to.
   */
  (
    interactiveFormSessionId: string,
    options?: SendUserMessageStreamedOptions
  ): Promise<
    NonNullablePaths<
      SendUserMessageStreamedResponse,
      | `responseChunk.textDetails.text`
      | `responseChunk.textDetails.style`
      | `responseChunk.textDataDetails.fieldTarget`
      | `responseChunk.textDataDetails.text`
      | `responseChunk.textDataDetails.displayValue`
      | `responseChunk.multiSelectInputDetails.fieldTarget`
      | `responseChunk.multiSelectInputDetails.options`
      | `responseChunk.multiSelectInputDetails.options.${number}.value`
      | `responseChunk.multiSelectInputDetails.options.${number}.label`
      | `responseChunk.multiSelectInputDetails.allowedCustomValue.description`
      | `responseChunk.numberInputDetails.fieldTarget`
      | `responseChunk.separatorDetails.type`
      | `responseChunk.singleSelectInputDetails.fieldTarget`
      | `responseChunk.singleSelectInputDetails.options`
      | `responseChunk.errorDetails.messageText`
      | `responseChunk.submissionDetails.submissionId`
      | `responseChunk.importantTextDetails.fieldTarget`
      | `responseChunk.importantTextDetails.text`
      | `responseChunk.endOfResponseDetails.success`
      | `responseChunk.fileUploadDetails.fieldTarget`
      | `responseChunk.signatureDetails.fieldTarget`
      | `responseChunk.chunkType`
      | `responseChunk.meaningfulInput.startOffset`
      | `responseChunk.meaningfulInput.length`
      | `interactiveFormSession._id`
      | `interactiveFormSession.formId`,
      6
    > & {
      __applicationErrorsType?: SendUserMessageStreamedApplicationErrors;
    }
  >;
}

export function generateFormSummary(
  httpClient: HttpClient
): GenerateFormSummarySignature {
  return (form: Form) =>
    universalGenerateFormSummary(
      form,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateFormSummarySignature {
  /**
   * Generates an AI summary of a form's structure and purpose in natural language, based on the form's fields and configuration, of max 255 characters.
   *
   * The summary describes what the form collects and its intended use case,
   * useful for accessibility, previews, and AI-powered form discovery.
   * @param - Form object to generate a summary for.
   * Must include the form's fields and configuration.
   * @returns Response containing the generated form summary.
   */
  (form: Form): Promise<
    GenerateFormSummaryResponse & {
      __applicationErrorsType?: GenerateFormSummaryApplicationErrors;
    }
  >;
}

export const onInteractiveFormSessionCreated = EventDefinition(
  'wix.forms.ai.v1.interactive_form_session_created',
  true,
  (event: InteractiveFormSessionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.form.createdDate' },
            { path: 'entity.form.updatedDate' },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.metadata.updatedTimestamp',
            },
            { path: 'entity.form.limitationRule.dateTimeDeadline' },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.disabledFormMessage.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.disabledFormMessage.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.metadata.updatedTimestamp',
            },
            {
              path: 'entity.form.quizSettings.message.metadata.createdTimestamp',
            },
            {
              path: 'entity.form.quizSettings.message.metadata.updatedTimestamp',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.form.fields.validation.number.maximum' },
            { path: 'entity.form.fields.validation.number.minimum' },
            { path: 'entity.form.fields.validation.number.multipleOf' },
            {
              path: 'entity.form.fields.validation.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.fields.validation.array.items.number.maximum',
            },
            {
              path: 'entity.form.fields.validation.array.items.number.minimum',
            },
            {
              path: 'entity.form.fields.validation.array.items.number.multipleOf',
            },
            {
              path: 'entity.form.fields.validation.array.items.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.fields.validation.array.items.object.properties.*.number.maximum',
            },
            {
              path: 'entity.form.fields.validation.array.items.object.properties.*.number.minimum',
            },
            {
              path: 'entity.form.fields.validation.array.items.object.properties.*.number.multipleOf',
            },
            {
              path: 'entity.form.fields.validation.array.items.object.properties.*.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.number.maximum',
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.number.minimum',
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.number.multipleOf',
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.array.items.number.maximum',
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.array.items.number.minimum',
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.array.items.number.multipleOf',
            },
            {
              path: 'entity.form.fields.validation.object.properties.*.array.items.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.default',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.validation.maximum',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.validation.minimum',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.validation.multipleOf',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.validation.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.default',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.formFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
            },
            { path: 'entity.form.deletedFields.validation.number.maximum' },
            { path: 'entity.form.deletedFields.validation.number.minimum' },
            { path: 'entity.form.deletedFields.validation.number.multipleOf' },
            {
              path: 'entity.form.deletedFields.validation.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.number.maximum',
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.number.minimum',
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.number.multipleOf',
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.object.properties.*.number.maximum',
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.object.properties.*.number.minimum',
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.object.properties.*.number.multipleOf',
            },
            {
              path: 'entity.form.deletedFields.validation.array.items.object.properties.*.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.number.maximum',
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.number.minimum',
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.number.multipleOf',
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.array.items.number.maximum',
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.array.items.number.minimum',
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.array.items.number.multipleOf',
            },
            {
              path: 'entity.form.deletedFields.validation.object.properties.*.array.items.number.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.servicesDropdownOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.default',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.validation.maximum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.validation.minimum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.validation.multipleOf',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.validation.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.booleanOptions.checkboxOptions.label.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.servicesCheckboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.maximum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.minimum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.maximum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.minimum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.validation.items.objectOptions.properties.*.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.maximum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.minimum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.maximum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.minimum',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.multipleOf',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectOptions.validation.properties.*.arrayOptions.items.numberOptions.enum',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.default',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.schedulingOptions.appointmentOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.addressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.multilineAddressOptions.multilineAddressOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.objectArrayOptions.repeaterOptions.description.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.deletedFormFields.displayOptions.richContentOptions.richContent.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.submitSettings.thankYouMessageOptions.richContent.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.form.disabledFormMessage.nodes.tocData.fontSize' },
            {
              path: 'entity.form.disabledFormMessage.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.disabledFormMessage.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.quizSettings.passingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.passMessage.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.fontSize',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.quizSettings.scoreAndPassingStatusOptions.failMessage.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.imageData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.videoData.video.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.form.quizSettings.message.nodes.tocData.fontSize' },
            {
              path: 'entity.form.quizSettings.message.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.form.quizSettings.message.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.quizFieldSettings.score',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.quizFieldSettings.score',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
              isRepeated: true,
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.quizFieldSettings.score',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.quizFieldSettings.score',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.score',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.quizFieldSettings.correctAnswersList.correctAnswers',
              isRepeated: true,
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.quizFieldSettings.score',
            },
            { path: 'entity.form.quizSettings.passingScorePoints' },
            { path: 'entity.form.quizSettings.passingScorePercentage' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.textInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.radioGroupOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dropdownOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateTimeOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.phoneInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.dateInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.timeInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.datePickerOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.stringOptions.passwordOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.numberInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.numberOptions.ratingInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.options.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.arrayOptions.tagsOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.fileUploadOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.wixFileOptions.signatureOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.donationInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.paymentInputOptions.media.image',
            },
            {
              path: 'entity.form.formFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.textInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.radioGroupOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dropdownOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateTimeOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.phoneInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.dateInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.timeInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.datePickerOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.stringOptions.passwordOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.numberInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.numberOptions.ratingInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.options.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.checkboxGroupOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.options.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.arrayOptions.tagsOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.fileUploadOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.wixFileOptions.signatureOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.options.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.checkboxGroupOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.donationInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.paymentInputOptions.media.image',
            },
            {
              path: 'entity.form.deletedFormFields.inputOptions.paymentOptions.fixedPaymentOptions.media.image',
            },
          ],
        },
      ])
    )
)<InteractiveFormSessionCreatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  AcknowledgmentResponse,
  ActionEvent,
  Address,
  AddressComponentTypeOptionsOneOf,
  AddressInfo,
  AddressInfoTag,
  AddressLine2,
  Alignment,
  AllowedValuesOptions,
  AnchorData,
  AndCondition,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  Appointment,
  AppointmentFormatInfoOneOf,
  ArrayComponentType,
  ArrayErrorMessages,
  ArrayItems,
  ArrayItemsItemTypeOptionsOneOf,
  ArrayType,
  ArrayTypeArrayItems,
  ArrayTypeArrayItemsItemsOneOf,
  AspectRatio,
  AssistantOptions,
  AudioData,
  Availability,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Banner,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  BooleanComponentType,
  BooleanErrorMessages,
  BooleanType,
  Border,
  BorderColors,
  BorderWidths,
  BreakPoint,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  Call,
  CallToolsRequest,
  CallToolsResponse,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CardStylesAlignment,
  CardStylesType,
  CellStyle,
  ChangeableProperty,
  Checkbox,
  CheckboxGroup,
  CheckboxGroupOption,
  ChunkType,
  ClientTime,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  ComponentType,
  ComponentsTags,
  Condition,
  ConditionNode,
  ConditionNodeNodeOneOf,
  ConfirmationLevel,
  ContactAutofill,
  ContactField,
  ContextMessage,
  ContextMessageOptionsOneOf,
  ConverseRequest,
  ConverseRequestRequestOneOf,
  ConverseResponse,
  ConverseResponseResponseOneOf,
  CorrectAnswersList,
  CreateInteractiveFormSessionOptions,
  CreateInteractiveFormSessionRequest,
  CreateInteractiveFormSessionResponse,
  CreateInteractiveFormSessionStreamedOptions,
  CreateInteractiveFormSessionStreamedRequest,
  CreateInteractiveFormSessionStreamedResponse,
  Crop,
  CustomFieldInfo,
  CustomOption,
  CustomValue,
  DataExtensionsDetails,
  DateInput,
  DatePicker,
  DateRange,
  DateTimeAdvancedConstraints,
  DateTimeConstraints,
  DateTimeInput,
  DayOfWeek,
  DebugDetails,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DefaultCountryConfig,
  DefaultCountryConfigOptionsOneOf,
  DefaultCountryConfigType,
  Design,
  DesignTarget,
  DeveloperOptions,
  Dimensions,
  Direction,
  DisplayField,
  DisplayFieldDisplayFieldTypeOptionsOneOf,
  DisplayFieldType,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  DonationInput,
  DonationInputOption,
  Dropdown,
  DropdownOption,
  DynamicPriceOptions,
  EmailInfo,
  EmailInfoTag,
  EmbedData,
  EndOfResponseDetails,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ErrorDetails,
  EventData,
  EventMetadata,
  ExtendedFields,
  Field,
  FieldFieldTypeOptionsOneOf,
  FieldGroup,
  FieldOverride,
  FieldOverridePropertyTypeOptionsOneOf,
  FieldOverrides,
  FieldType,
  FieldsOverrides,
  FieldsSettings,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FileType,
  FileUpload,
  FileUploadDetails,
  FirstDayOfWeek,
  FixedPayment,
  FixedPriceOptions,
  FontFamilyData,
  FontSizeData,
  FontType,
  Form,
  FormField,
  FormFieldArrayType,
  FormFieldBooleanType,
  FormFieldContactInfo,
  FormFieldContactInfoAdditionalInfoOneOf,
  FormFieldContactInfoAddressInfo,
  FormFieldContactInfoContactField,
  FormFieldContactInfoCustomFieldInfo,
  FormFieldContactInfoEmailInfo,
  FormFieldContactInfoEmailInfoTag,
  FormFieldContactInfoPhoneInfo,
  FormFieldContactInfoPhoneInfoTag,
  FormFieldContactInfoSubscriptionInfo,
  FormFieldNumberType,
  FormFieldObjectType,
  FormFieldStringType,
  FormFieldStringTypeFormatOptionsOneOf,
  FormLayout,
  FormOverride,
  FormProperties,
  FormRule,
  Format,
  FormatEnumFormat,
  FunctionCallOptions,
  FunctionCallOutputOptions,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GenerateFormSummaryRequest,
  GenerateFormSummaryResponse,
  GetStateRequest,
  GetStateResponse,
  Gradient,
  GradientType,
  Group,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  HiddenOptions,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImageFit,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  ImportantTextDetails,
  InPersonOptions,
  Indentation,
  InitialExpandedItems,
  InputField,
  InputFieldInputTypeOptionsOneOf,
  InputType,
  IntegerType,
  InteractiveFormSession,
  InteractiveFormSessionCreatedEnvelope,
  InteractiveFormSessionResponseChunk,
  InteractiveFormSessionResponseChunkOfOneOf,
  Item,
  ItemDataOneOf,
  ItemLayout,
  ItemLayoutItemOneOf,
  ItemStyle,
  ItemType,
  Kind,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutDataImagePosition,
  LayoutType,
  LimitationRule,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  LinkTarget,
  ListStyle,
  ListValue,
  Location,
  LocationLocationInfoOneOf,
  MapData,
  MapSettings,
  MapType,
  Margin,
  MeaningfulInput,
  Media,
  MediaItem,
  MediaItemMediaOneOf,
  MediaSettings,
  MeetingType,
  MentionData,
  MessageEnvelope,
  Metadata,
  MultiSelectInputDetails,
  MultilineAddress,
  MultilineAddressValidation,
  NestedForm,
  NestedFormFieldOverrides,
  NestedFormOverrides,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  NumberComponentType,
  NumberCorrectAnswersList,
  NumberErrorMessages,
  NumberInput,
  NumberInputDetails,
  NumberOfColumns,
  NumberQuizFieldSettings,
  NumberRangeLimit,
  NumberRangeLimitEndOneOf,
  NumberType,
  ObjectArray,
  ObjectArrayComponentType,
  ObjectArrayComponentTypeOptionsOneOf,
  ObjectArrayType,
  ObjectErrorMessages,
  ObjectType,
  ObjectTypePropertiesType,
  ObjectTypePropertiesTypePropertiesTypeOneOf,
  Oembed,
  Operator,
  OptInLevel,
  Option,
  OptionDesign,
  OptionLayout,
  OrCondition,
  OrderedListData,
  Orientation,
  Origin,
  OutputInteractiveFormSessionStreamedRequest,
  OutputInteractiveFormSessionStreamedResponse,
  OverrideEntityType,
  OverrideEntityTypeEnumOverrideEntityType,
  PDFSettings,
  PageNavigationOptions,
  ParagraphData,
  PassFailMessages,
  Password,
  Payment,
  PaymentComponentType,
  PaymentComponentTypeOptionsOneOf,
  PaymentInput,
  PaymentType,
  Permissions,
  PhoneConstraints,
  PhoneInfo,
  PhoneInfoTag,
  PhoneInput,
  PhoneOptions,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollOption,
  PopupOptions,
  Position,
  PostSubmissionTriggers,
  PredefinedValidation,
  PredefinedValidationFormatOptionsOneOf,
  PriceType,
  PricingData,
  Product,
  ProductCheckboxGroup,
  ProductCheckboxGroupOption,
  ProductPriceOptionsOneOf,
  ProductType,
  PropertiesType,
  PropertiesTypeEnum,
  PropertiesTypePropertiesTypeOptionsOneOf,
  QuantityLimit,
  QuizFieldSettings,
  QuizSettings,
  QuizSettingsPassingCriteriaOneOf,
  QuizSettingsResultsDisplayOptionsOneOf,
  RadioGroup,
  RadioGroupOption,
  RatingInput,
  RedirectOptions,
  Rel,
  RelayAssistantMessageRequest,
  Repeater,
  ReportConversationRequest,
  ReportConversationResponse,
  RequiredIndicator,
  RequiredIndicatorPlacement,
  RequiredIndicatorProperties,
  RequiredOptions,
  Resizing,
  Response,
  ResponsivenessBehaviour,
  RestoreInfo,
  ResultsDisplay,
  RibbonStyles,
  RichContent,
  RichContentOptions,
  Role,
  Rule,
  RuleFormOverride,
  RuleFormOverrideEntityTypeOptionsOneOf,
  Scaling,
  Scheduling,
  SchedulingComponentType,
  SchedulingComponentTypeOptionsOneOf,
  Section,
  SendUserMessageOptions,
  SendUserMessageRequest,
  SendUserMessageResponse,
  SendUserMessageStreamedOptions,
  SendUserMessageStreamedRequest,
  SendUserMessageStreamedResponse,
  SeparatorDetails,
  ServiceOption,
  ServicesCheckboxGroup,
  ServicesDropdown,
  Settings,
  ShapeData,
  ShapeDataStyles,
  Signature,
  SignatureDetails,
  SingleSelectInputDetails,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  Source,
  SpamFilterProtectionLevel,
  Spoiler,
  SpoilerData,
  StaffStrategySelection,
  Step,
  Stop,
  StringComponentType,
  StringCorrectAnswersList,
  StringErrorMessages,
  StringQuizFieldSettings,
  StringType,
  StringTypeDateTimeConstraints,
  StringTypeFormatEnumFormat,
  StringTypeFormatOptionsOneOf,
  StringTypePhoneConstraints,
  StringTypeValidationMessages,
  Style,
  Styles,
  StylesBorder,
  StylesPosition,
  SubmissionAccess,
  SubmissionDetails,
  SubmitSettings,
  SubmitSettingsSubmitSuccessActionOptionsOneOf,
  SubmitSuccessAction,
  SubscriptionChannel,
  SubscriptionInfo,
  TableCellData,
  TableData,
  Tag,
  TagList,
  Tags,
  TagsOption,
  Target,
  TextAlignment,
  TextData,
  TextDataDetails,
  TextDetails,
  TextInput,
  TextNodeStyle,
  TextStyle,
  ThankYouMessageOptions,
  Thumbnails,
  ThumbnailsAlignment,
  TimeInput,
  TocData,
  Tool,
  Type,
  UploadFileFormat,
  UpsertContact,
  Usage,
  UsageRequest,
  UserOptions,
  V4FormFieldContactInfo,
  V4FormFieldContactInfoAdditionalInfoOneOf,
  Validation,
  ValidationFormat,
  ValidationMessages,
  ValidationValidationOneOf,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoConferenceOptions,
  VideoData,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  WixFile,
  WixFileComponentType,
  WixFileComponentTypeOptionsOneOf,
  _Array,
  _ArrayComponentTypeOptionsOneOf,
  _Boolean,
  _BooleanComponentTypeOptionsOneOf,
  _Number,
  _NumberComponentTypeOptionsOneOf,
  _Object,
  _String,
  _StringComponentTypeOptionsOneOf,
} from './forms-ai-v1-interactive-form-session-interactive-form-sessions.universal.js';
